// src/components/MapComponent.js
import React, { useState, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Typography } from '@mui/material';

const MapComponent = ({ 
  onLocationSelect, 
  initialLat = 24.7136, 
  initialLng = 46.6753,
  height = '400px',
  markers = [],
  showUserLocation = true,
  readOnly = false,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [lng, setLng] = useState(initialLng);
  const [lat, setLat] = useState(initialLat);
  const [zoom, setZoom] = useState(13);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);

  // ===== تهيئة الخريطة =====
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    console.log('🗺️ تهيئة الخريطة...');

    try {
      // استخدام أسلوب رسم الخرائط من OpenStreetMap مباشرة
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'osm': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap',
            }
          },
          layers: [{
            id: 'osm-layer',
            type: 'raster',
            source: 'osm',
          }]
        },
        center: [lng, lat],
        zoom: zoom,
        maxZoom: 19,
        minZoom: 1,
      });

      // إضافة عناصر التحكم
      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.current.addControl(new maplibregl.FullscreenControl(), 'top-right');
      map.current.addControl(new maplibregl.ScaleControl(), 'bottom-right');

      // ===== عند تحميل الخريطة =====
      map.current.on('load', () => {
        console.log('✅ تم تحميل الخريطة بنجاح');
        setMapLoaded(true);
        setError(null);
      });

      // ===== عند حدوث خطأ =====
      map.current.on('error', (e) => {
        console.error('❌ خطأ في الخريطة:', e);
        // لا نعرض خطأ للمستخدم لتجنب الإزعاج
      });

      // ===== عند تغيير مستوى التكبير =====
      map.current.on('zoomend', () => {
        const currentZoom = map.current.getZoom();
        console.log('🔍 مستوى التكبير:', currentZoom);
      });

      // ===== عند النقر على الخريطة =====
      if (!readOnly) {
        map.current.on('click', (e) => {
          const { lng, lat } = e.lngLat;
          setLng(lng);
          setLat(lat);
          
          // إزالة الماركر السابق
          if (map.current._marker) {
            map.current._marker.remove();
          }
          
          // إضافة ماركر جديد
          const marker = new maplibregl.Marker({ color: '#FF1493', draggable: false })
            .setLngLat([lng, lat])
            .addTo(map.current);
          map.current._marker = marker;
          
          // استدعاء دالة الـ Callback مع الإحداثيات
          if (onLocationSelect) {
            onLocationSelect({ lat, lng });
          }
        });
      }

      // ===== إضافة الماركرات الافتراضية =====
      setTimeout(() => {
        if (map.current && markers.length > 0) {
          markers.forEach((markerData) => {
            const markerEl = document.createElement('div');
            markerEl.className = 'custom-marker';
            markerEl.innerHTML = markerData.color === '#FF1493' ? '📍' : '🏁';
            markerEl.style.fontSize = '28px';
            markerEl.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
            
            new maplibregl.Marker({ element: markerEl })
              .setLngLat([markerData.lng, markerData.lat])
              .setPopup(new maplibregl.Popup({ offset: 25 })
                .setHTML(`<div style="padding:8px;font-family:Arial;text-align:center;">
                  <strong>${markerData.title || 'موقع'}</strong>
                  ${markerData.description ? `<br><span style="font-size:12px;color:#666;">${markerData.description}</span>` : ''}
                </div>`))
              .addTo(map.current);
          });
        }
      }, 500);

      // ===== تحديد موقع المستخدم =====
      if (showUserLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setLat(latitude);
            setLng(longitude);
            
            if (map.current) {
              map.current.flyTo({
                center: [longitude, latitude],
                zoom: 14,
                essential: true,
              });
              
              // إضافة ماركر للمستخدم
              const userMarkerEl = document.createElement('div');
              userMarkerEl.innerHTML = '📍';
              userMarkerEl.style.fontSize = '32px';
              userMarkerEl.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
              
              new maplibregl.Marker({ element: userMarkerEl })
                .setLngLat([longitude, latitude])
                .setPopup(new maplibregl.Popup({ offset: 25 })
                  .setHTML('<div style="padding:8px;font-family:Arial;"><strong>📍 موقعك الحالي</strong></div>'))
                .addTo(map.current);
            }
          },
          (error) => {
            console.warn('⚠️ لا يمكن تحديد الموقع:', error.message);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      }

    } catch (err) {
      console.error('❌ خطأ في تهيئة الخريطة:', err);
      setError('حدث خطأ في تهيئة الخريطة');
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // ===== تحديث الإحداثيات عند تغيرها =====
  useEffect(() => {
    if (map.current && mapLoaded) {
      map.current.flyTo({
        center: [lng, lat],
        duration: 1000,
        essential: true,
      });
    }
  }, [lng, lat, mapLoaded]);

  return (
    <div style={{ position: 'relative', width: '100%', height: height }}>
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '100%', 
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid rgba(255, 105, 180, 0.15)',
          backgroundColor: '#f0f0f0',
        }} 
      />
      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255,255,255,0.9)',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          minWidth: '200px',
        }}>
          <Typography color="error" variant="body1">⚠️ {error}</Typography>
          <Typography variant="caption" color="text.secondary">يرجى تحديث الصفحة</Typography>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
